import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../app";
import { useCallback, useEffect } from "react";
import { setLoading, setUser } from "../store/authProviderSlice";
import oidcManager from "../utils/oidcManager";
import {
  ACCESS_TOKEN_COOKIE,
  ID_TOKEN_COOKIE,
  PREVIOUS_PATH_KEY,
  REFRESH_TOKEN_COOKIE,
} from "../constants";
import { getVariableFromEnv } from "../../common/utilities/env";
import { parseJwt } from "../utils/auth";
import useAuthenticationTokens from "./useAuthenticationTokens";

// Global promise to prevent concurrent refresh attempts
let refreshPromise: Promise<string | null> | null = null;

const useOidcAuth = (loadUserOnInit: boolean = false) => {
  const dispatch = useDispatch();

  const { setToken, getToken, clearAllTokens, isTokenExpired } =
    useAuthenticationTokens();

  const { user, isLoading } = useSelector(
    (state: RootState) => state.authProviderReducer,
  );

  const login = async (previousPath: string = "/") => {
    try {
      sessionStorage.setItem(PREVIOUS_PATH_KEY, previousPath);
      await oidcManager.signinRedirect();
    } catch (error) {
      console.error("Error during login:", error);
    }
  };

  const loginWithRedirect = async (
    invitation: string,
    organization: string,
  ) => {
    try {
      await oidcManager.signinRedirect({
        extraQueryParams: {
          invitation,
          organization,
        },
      });
    } catch (error) {
      console.error("Error during login redirect:", error);
    }
  };

  const logout = useCallback(async () => {
    try {
      const idToken = getToken(ID_TOKEN_COOKIE);
      clearAllTokens();
      dispatch(setUser(null));

      if (idToken) {
        await oidcManager.signoutRedirect({ id_token_hint: idToken });
      } else {
        // ForgeRock rejects endSession without id_token_hint; nothing to revoke server-side.
        window.location.replace("/login");
      }
    } catch (error) {
      console.error("Error during logout:", error);
    }
  }, [dispatch, clearAllTokens, getToken]);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    // If a refresh is already in progress, return the existing promise
    if (refreshPromise) {
      return refreshPromise;
    }

    // Create a new refresh promise
    refreshPromise = (async () => {
      try {
        const refreshToken = getToken(REFRESH_TOKEN_COOKIE);
        if (!refreshToken) {
          console.error("No refresh token available");
          return null;
        }

        const clientId = getVariableFromEnv("AUTH_CLIENT_ID");
        const tokenEndpoint =
          await oidcManager.metadataService.getTokenEndpoint(false);

        const response = await fetch(tokenEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: clientId,
            refresh_token: refreshToken,
          }),
        });

        if (!response.ok) {
          console.error("Token refresh failed");
          return null;
        }

        const tokens = await response.json();

        if (tokens.access_token) {
          setToken(ACCESS_TOKEN_COOKIE, tokens.access_token);

          if (tokens.refresh_token) {
            setToken(REFRESH_TOKEN_COOKIE, tokens.refresh_token);
          }

          if (tokens.id_token) {
            setToken(ID_TOKEN_COOKIE, tokens.id_token);

            const userInfo = parseJwt(tokens.id_token);
            if (userInfo) {
              dispatch(
                setUser({
                  ...userInfo,
                  access_token: tokens.access_token,
                  expires_at: userInfo.exp,
                  expired: false,
                }),
              );
            }
          }

          return tokens.access_token;
        }

        console.error("No access token in response");
        return null;
      } catch (error) {
        console.error("Token refresh error:", error);
        return null;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }, [getToken, setToken, dispatch]);

  const getAccessTokenSilently = useCallback(
    async (alwaysGetNewToken = false): Promise<string | null> => {
      try {
        const accessToken = getToken(ACCESS_TOKEN_COOKIE);

        if (!alwaysGetNewToken && accessToken && !isTokenExpired(accessToken)) {
          return accessToken;
        }

        return await refreshAccessToken();
      } catch (error) {
        console.error("Silent token renewal failed", error);
        return null;
      }
    },
    [getToken, isTokenExpired, refreshAccessToken],
  );

  // On initial load, check if the user is already authenticated and set user state
  useEffect(() => {
    const loadUser = async () => {
      try {
        dispatch(setLoading(true));

        // getToken returns null for expired access/id tokens.
        const accessToken = getToken(ACCESS_TOKEN_COOKIE);
        const idToken = getToken(ID_TOKEN_COOKIE);
        const userInfo = idToken ? parseJwt(idToken) : null;

        if (accessToken && userInfo) {
          dispatch(
            setUser({
              ...userInfo,
              access_token: accessToken,
              expires_at: userInfo.exp,
              expired: false,
            }),
          );
          return;
        }

        if (!getToken(REFRESH_TOKEN_COOKIE)) {
          console.warn("No valid access token or refresh token, logging out");
          dispatch(setUser(null));
          return;
        }

        const newToken = await refreshAccessToken();
        if (!newToken) {
          console.warn("Token refresh failed, user not authenticated");
          dispatch(setUser(null));
        }
      } catch (error) {
        console.error("Error loading user:", error);
        dispatch(setUser(null));
      } finally {
        dispatch(setLoading(false));
      }
    };

    if (loadUserOnInit) {
      loadUser();
    }
  }, [dispatch, loadUserOnInit, getToken, refreshAccessToken]);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    loginWithRedirect,
    logout,
    getAccessTokenSilently,
    refreshAccessToken,
  };
};

export default useOidcAuth;
